import { useState } from "react";
import Modal from "./Modal";
import type { Deal } from "../../../lib/types";
import { scheduleBuild } from "../../../lib/dispatchActions";
import { useAuth } from "../../../context/AuthContext";
import PreFindingsCard from "../../deal/PreFindingsCard";
import AddOnNotesCard from "../../deal/AddOnNotesCard";
import { buildEndProblem, fmtBuildSpan, normalizeBuildEnd } from "../../../lib/buildCalendar";

interface Props {
  open: boolean;
  deal: Deal;
  onClose: () => void;
  onCompleted: () => void;
}

export default function ScheduleBuildModal({
  open,
  deal,
  onClose,
  onCompleted,
}: Props) {
  const { member } = useAuth();
  const [date, setDate] = useState(deal.ho_preferred_build_date ?? "");
  // Oct 26, 2026: the last day of a multi-day build ("" = one day).
  const [endDate, setEndDate] = useState("");
  const endISO = date ? normalizeBuildEnd(date, endDate || null) : null;
  const endProblem = date && endDate ? buildEndProblem(date, endDate) : null;
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isWeekend = (() => {
    if (!date) return false;
    const d = new Date(date + "T12:00:00");
    const day = d.getDay();
    return day === 0 || day === 6;
  })();

  async function submit() {
    setErr(null);
    if (!date) return setErr("Pick a build date.");
    if (isWeekend) return setErr("Builds cannot be scheduled on weekends.");
    if (endProblem) return setErr(endProblem);
    setSubmitting(true);
    try {
      await scheduleBuild(deal, { date, notes, endDate: endISO }, member?.id ?? null);
      onCompleted();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Schedule build"
      subtitle={`${deal.homeowner_name ?? "(no name)"} · ${deal.property_street ?? ""}`}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Schedule build"}
          </button>
        </>
      }
    >
      {/* Surface PRE findings so Robert sees gutter guards / siding rot /
          satellite Y-flags before picking a build date. */}
      <PreFindingsCard deal={deal} className="mb-3" />
      <AddOnNotesCard deal={deal} className="mb-3" />

      <div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              First build day
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setEndDate(""); }}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Last build day
            </label>
            <input
              type="date"
              value={endDate || date}
              min={date || undefined}
              disabled={!date}
              onChange={(e) => setEndDate(e.target.value && e.target.value !== date ? e.target.value : "")}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm disabled:bg-slate-50"
            />
          </div>
        </div>
        {endProblem && <p className="text-xs text-red-600 mt-1">{endProblem}</p>}
        {endISO && !endProblem && <p className="text-xs text-slate-500 mt-1">{fmtBuildSpan(date, endISO)}</p>}
        {isWeekend && (
          <p className="text-xs text-red-600 mt-1">No weekend scheduling — pick a weekday.</p>
        )}
        {deal.ho_preferred_build_date && (
          <p className="text-xs text-slate-500 mt-1">
            Homeowner preferred: {deal.ho_preferred_build_date}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Notes <span className="text-slate-400">(optional)</span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          placeholder="Crew, squares, special instructions..."
        />
      </div>

      <p className="text-xs text-slate-500">
        Stage advances to build_scheduled. Homeowner + RM + SRS get the build
        confirmation.
      </p>

      {err && (
        <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>
      )}
    </Modal>
  );
}
